import { Auth0Provider } from '@auth0/auth0-react';
import type { ReactNode } from 'react';
import { useConfig } from '@/config/ConfigProvider';
import { createAuth0Config, validateAuth0Config } from './auth0-config';
import { TestAuthProvider } from './test-auth-provider';
import { isE2ETestMode } from './utils/e2e';

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const config = useConfig();

  // Check for E2E test mode (SSR-safe: checks build-time VITE_E2E_TEST_MODE first,
  // then runtime window.__ENV__ if available)
  const isE2EMode = isE2ETestMode();

  // Also treat demo-mode config values as demo mode (when config wasn't injected)
  const isDemoModeConfig =
    config.auth0Domain === 'demo-mode' &&
    config.auth0ClientId === 'demo-mode' &&
    config.auth0Audience === 'demo-mode';

  // Use test auth provider for E2E tests or demo mode
  if (isE2EMode || isDemoModeConfig) {
    return <TestAuthProvider>{children}</TestAuthProvider>;
  }

  // Validate configuration in development
  if (import.meta.env.DEV) {
    validateAuth0Config(config);
  }

  const auth0Config = createAuth0Config(config);

  return <Auth0Provider {...auth0Config}>{children}</Auth0Provider>;
};
